import { HealthCheckRevisionWeight } from '../Onboarding/ContentInventory/Components/HealthCheckReferenceData';

/** A check of a page that has a score there, and the revision of the check the score was computed with. */
export interface IScoredHealthCheck {
  healthCheckKey: string;
  /** The revision the page's entry names; a figure that names none cannot be traced to a weight. */
  healthCheckRevisionId?: string;
}

/** The checks of a page that count towards its health, and the weight of each revision they were computed with. */
export interface IHealthScoreSharesInput {
  /** The checks with a score on the page: a check without one does not count towards the page's health. */
  scoredHealthChecks: IScoredHealthCheck[];
  /** What Sync Core answered for each revision, by its id; a revision not answered yet is absent. */
  weightByRevisionId: Record<string, HealthCheckRevisionWeight>;
}

/** What the Weight column says of one check. */
export type HealthScoreShare =
  /** The check's share of the page's health, in percent. */
  | { kind: 'share'; share: number }
  /** A weight the shares are taken over is still being read, so no share is known yet. */
  | { kind: 'loading' }
  /** The weight of the revision the figure names could not be read. */
  | { kind: 'not-loaded' }
  /** The figure names no revision of its check, so no weight can be read for it. */
  | { kind: 'not-named' };

export interface IHealthScoreShares {
  /** Every scored check, by its key; a check without a score has no entry: it does not count. */
  byHealthCheckKey: Record<string, HealthScoreShare>;
  /** How many scored checks the shares leave out, because their weight could not be read or cannot be read. */
  leftOut: number;
}

/**
 * How much each check counts towards a page's health, as a percentage of it.
 *
 * A page's health is the weighted mean of its checks' scores: each check with a score counts by the weight of
 * the revision of the check its figure was computed with, out of the weights of every check with a score. So a
 * check's share is that weight over that total, read from the very revision the figure names, so a check edited
 * since the page was measured still shows the weight it was measured with. A share is a part of the total of every
 * weight, so while one weight is still being read every share waits, and none shows a figure that can still change;
 * a check whose weight could not be read or cannot be read says which, and the others' shares are taken over the
 * weights that are known.
 */
export function toHealthScoreShares({ scoredHealthChecks, weightByRevisionId }: IHealthScoreSharesInput): IHealthScoreShares {
  const byHealthCheckKey: Record<string, HealthScoreShare> = {};
  const reading = scoredHealthChecks.some(
    ({ healthCheckRevisionId }) => !!healthCheckRevisionId && !weightByRevisionId[healthCheckRevisionId]
  );
  const known = scoredHealthChecks.filter(
    ({ healthCheckRevisionId }) => !!healthCheckRevisionId && !!weightByRevisionId[healthCheckRevisionId]?.read
  );
  // Every figure counts, as the health counts every entry the page holds.
  const total = known.reduce((sum, { healthCheckRevisionId }) => sum + (weightByRevisionId[healthCheckRevisionId ?? '']?.weight ?? 0), 0);

  for (const { healthCheckKey, healthCheckRevisionId } of scoredHealthChecks) {
    if (!healthCheckRevisionId) {
      byHealthCheckKey[healthCheckKey] = { kind: 'not-named' };
      continue;
    }

    const weight = weightByRevisionId[healthCheckRevisionId];
    if (weight && !weight.read) {
      byHealthCheckKey[healthCheckKey] = { kind: 'not-loaded' };
      continue;
    }
    if (!weight || reading) {
      byHealthCheckKey[healthCheckKey] = { kind: 'loading' };
      continue;
    }
    if (!(total > 0)) {
      byHealthCheckKey[healthCheckKey] = { kind: 'not-loaded' };
      continue;
    }

    const previous = byHealthCheckKey[healthCheckKey];
    const share = ((weight.weight ?? 0) / total) * 100 + (previous?.kind === 'share' ? previous.share : 0);
    byHealthCheckKey[healthCheckKey] = { kind: 'share', share };
  }

  const leftOut = scoredHealthChecks.filter(
    ({ healthCheckRevisionId }) => !healthCheckRevisionId || weightByRevisionId[healthCheckRevisionId]?.read === false
  ).length;
  return { byHealthCheckKey, leftOut };
}
